// Activity-log card — fixed-height tail of the most recent N events
// from agent_logs. Auto-updates on every tick (no scroll-pause needed
// at the card level; the popup handles full scroll-pause behaviour).
//
// Per design_card_log (workstream `tui`).
//
// Aesthetic: rounded border, dim border, section header inset; verb
// verb coloured cyan; prose from the shared renderOp formatter.
//
// Rows are column-aligned via src/cli/tui/columns.ts. Per
// feat_column_aligned_lists clipping policy: timestamp, source, verb
// are PROTECTED (short, identity-bearing); the rest is CLIPPABLE.

import type { ReactElement } from "react";
import { renderOp } from "../../../log-render.js";
import type { WorkstreamSnapshot } from "../../../state.js";
import {
  type ColumnSpec,
  contentWidthFromCols,
  layoutColumns,
  renderRow,
  termColsForLayout,
} from "../columns.js";
import { CARD_CONFIGS, cardRenderHeight } from "../layout.js";
import { ListRow } from "../list-row.js";
import { TitledBox } from "../titled-box.js";
import { CardPlaceholder } from "./_placeholder.js";

export interface LogCardProps {
  snapshot: WorkstreamSnapshot | null;
  rowBudget?: number;
  cols?: number;
}

export const cardConfig = CARD_CONFIGS[4];

const COLUMN_SPECS: ReadonlyArray<ColumnSpec> = [
  { kind: "protect" }, // ts (HH:MM:SS)
  { kind: "protect" }, // source
  { kind: "protect" }, // verb (or '·' fallback)
  { kind: "clip", min: 1 }, // rest / payload
];

export function LogCard({ snapshot, rowBudget, cols }: LogCardProps): ReactElement {
  const contentWidth = contentWidthFromCols(cols ?? termColsForLayout());
  if (snapshot === null) {
    return CardPlaceholder({
      title: "Activity log",
      cardId: 4,
      config: cardConfig,
      rowBudget,
      cols,
      text: "loading…",
    });
  }

  const { recent } = snapshot;

  if (recent.length === 0) {
    return CardPlaceholder({
      title: "Activity log",
      cardId: 4,
      config: cardConfig,
      rowBudget,
      cols,
      text: "(no events yet)",
    });
  }

  // Show the LAST N events (newest at the bottom). listLogs returns
  // them descending-by-seq via afterSeq cursor semantics; we slice
  // the head and reverse for "newest at bottom" reading.
  const tail = recent.slice(0, rowBudget ?? cardConfig.maxRows).reverse();

  // Prose comes from renderOp — the same formatter `mu log` uses. This
  // card owns column layout and colour, never phrasing (v2-log-verb).
  const cellRows = tail.map((row) => {
    const r = renderOp(row);
    const ts = row.createdAt.slice(11, 19);
    const verb = r?.verb ?? "·";
    const rest = r === null ? row.payload : [r.subject, r.detail].filter((x) => x !== "").join(" ");
    return [ts, row.source, verb, rest];
  });
  const widths = layoutColumns(cellRows, COLUMN_SPECS, contentWidth);

  return (
    <TitledBox
      height={cardRenderHeight(cardConfig, rowBudget)}
      width={cols}
      title="Activity log"
      subtitle={`last ↑${tail.length}`}
      cardId={4}
    >
      {tail.map((row, i) => {
        const cells = cellRows[i];
        if (cells === undefined) return null;
        const padded = renderRow(cells, widths, COLUMN_SPECS);
        const colors = [
          { dimColor: true }, // ts
          { dimColor: true }, // source
          row.intent === null ? { dimColor: true } : { color: "cyan" }, // verb
          undefined, // rest
        ];
        return <ListRow key={row.seq} cells={padded} contentWidth={contentWidth} colors={colors} />;
      })}
    </TitledBox>
  );
}
